/**
 * Phase PWA — Install prompt banner.
 *
 * Captures `beforeinstallprompt` on supported browsers (Chrome/Edge/
 * Samsung) and renders a dismissible amber banner inviting the user to
 * install the app. Only mounted on /manager and /customer routes per
 * Phase scope — admins use the browser menu if they want it.
 *
 * Dismiss state lives in localStorage with a 7-day TTL so the prompt
 * eventually reappears for users who weren't ready the first time. If
 * `appinstalled` fires, the LS flag flips permanently and the banner
 * never renders again on this browser.
 */
import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { isAlreadyInstalled } from "@/pwa/register";

const LS_DISMISS_KEY = "rp_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function wasRecentlyDismissed() {
    try {
        const v = window.localStorage.getItem(LS_DISMISS_KEY);
        if (!v) return false;
        const ts = Number(v);
        if (!ts) return false;
        return Date.now() - ts < DISMISS_TTL_MS;
    } catch { return false; }
}

export default function PwaInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(isAlreadyInstalled());
    const [dismissed, setDismissed] = useState(wasRecentlyDismissed());

    useEffect(() => {
        const onBefore = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed || dismissed || !deferred) return null;

    const onInstall = async () => {
        try {
            deferred.prompt();
            await deferred.userChoice;
        } catch { /* swallow */ }
        setDeferred(null);
    };
    const onDismiss = () => {
        try { window.localStorage.setItem(LS_DISMISS_KEY, String(Date.now())); }
        catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            data-testid="pwa-install-prompt"
            className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 flex items-center gap-3"
        >
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200">
                <Download size={14} />
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-950">Install the app</p>
                <p className="text-xs text-amber-900/80">
                    Get a faster experience and work offline. Inspections you start without signal will sync automatically.
                </p>
            </div>
            <button
                type="button"
                onClick={onInstall}
                data-testid="pwa-install-cta"
                className="inline-flex items-center gap-1 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 transition-colors"
            >
                Install
            </button>
            <button
                type="button"
                onClick={onDismiss}
                data-testid="pwa-install-dismiss"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-amber-700 hover:bg-amber-100 transition-colors"
                aria-label="Dismiss"
            >
                <X size={14} />
            </button>
        </div>
    );
}
